<template>
  <div class="p-6">
    <!-- Header -->
    <div class="flex justify-between items-center mb-6">
      <h1 class="text-2xl font-semibold text-gray-900">Gerenciamento de Fornecedores</h1>
      <button
        @click="openNewSupplierDialog"
        class="px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-dark/90"
      >
        Novo Fornecedor
      </button>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-8">
      <i class="pi pi-spin pi-spinner text-2xl text-blue-600"></i>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-8 text-red-600">
      {{ error }}
    </div>

    <!-- Empty State -->
    <div v-else-if="!suppliers || suppliers.length === 0" class="text-center py-8 text-gray-600">
      <p>Nenhum fornecedor encontrado</p>
    </div>

    <!-- Lista de Fornecedores -->
    <div v-else class="bg-white rounded-lg shadow overflow-hidden">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nome</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">CNPJ</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Telefone</th>
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cidade/Estado</th>
            <th class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Ações</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-200">
          <tr v-for="supplier in suppliers" :key="supplier.id" class="hover:bg-gray-50">
            <td class="px-6 py-4 whitespace-nowrap">{{ supplier.name }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ formatCNPJ(supplier.cnpj) }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ supplier.email }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ formatPhone(supplier.phone) }}</td>
            <td class="px-6 py-4 whitespace-nowrap">{{ supplier.city }}/{{ supplier.state }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
              <button 
                @click="editSupplier(supplier)"
                class="text-primary-dark hover:text-primary-dark/80"
                title="Editar fornecedor"
              >
                <i class="pi pi-pencil"></i>
              </button>
              <button 
                @click="confirmDeleteSupplier(supplier)"
                class="text-red-600 hover:text-red-900"
                title="Excluir fornecedor"
              >
                <i class="pi pi-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal Novo/Editar Fornecedor -->
    <Dialog
      v-model:visible="supplierDialog"
      modal
      header="Novo Fornecedor"
      :style="{ width: '80vw' }"
      :closable="true"
      @hide="hideDialog"
    >
      <form @submit.prevent="saveSupplier" class="space-y-6">
        <!-- Informações Básicas -->
        <div class="grid grid-cols-2 gap-4">
          <div>
            <label class="block text-sm font-medium text-gray-700">CNPJ</label>
            <input
              v-model="supplier.cnpj"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Nome</label>
            <input
              v-model="supplier.name"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Email</label>
            <input
              v-model="supplier.email"
              type="email"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
              required
            />
          </div>
          <div>
            <label class="block text-sm font-medium text-gray-700">Telefone</label>
            <input
              v-model="supplier.phone"
              type="text"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
              required
            />
          </div>
        </div>

        <!-- Endereço -->
        <div class="border-t pt-4">
          <h3 class="text-lg font-medium mb-4">Endereço</h3>
          <div class="grid grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-gray-700">CEP</label>
              <input
                v-model="supplier.address.cep"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Rua</label>
              <input
                v-model="supplier.address.street"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Número</label>
              <input
                v-model="supplier.address.number"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Complemento</label>
              <input
                v-model="supplier.address.complement"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Bairro</label>
              <input
                v-model="supplier.address.neighborhood"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Cidade</label>
              <input
                v-model="supplier.address.city"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                required
              />
            </div>
            <div>
              <label class="block text-sm font-medium text-gray-700">Estado</label>
              <input
                v-model="supplier.address.state"
                type="text"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                required
              />
            </div>
          </div>
        </div>

        <!-- Contas Bancárias -->
        <div class="border-t pt-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium">Contas Bancárias</h3>
            <button
              type="button"
              @click="addBankAccount"
              class="px-2 py-1 bg-primary-dark text-white rounded-md hover:bg-primary-dark/90 text-sm"
            >
              Adicionar Conta
            </button>
          </div>
          <div v-for="(account, index) in supplier.bankAccounts" :key="index" class="mb-4 p-4 bg-gray-50 rounded-lg relative">
            <button
              type="button"
              @click="removeBankAccount(index)"
              class="absolute top-2 right-2 text-red-600 hover:text-red-800"
            >
              <i class="pi pi-times"></i>
            </button>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Banco</label>
                <select
                  v-model="account.bank"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                >
                  <option value="">Selecione um banco</option>
                  <option v-for="bank in banks" :key="bank.code" :value="bank.code">
                    {{ bank.code }} - {{ bank.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Agência</label>
                <input
                  v-model="account.agency"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Conta</label>
                <input
                  v-model="account.account"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Tipo</label>
                <select
                  v-model="account.type"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                >
                  <option value="checking">Conta Corrente</option>
                  <option value="savings">Conta Poupança</option>
                </select>
              </div>
              <div class="col-span-2">
                <label class="block text-sm font-medium text-gray-700">Chave PIX</label>
                <input
                  v-model="account.pixKey"
                  type="text"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Centros de Custo -->
        <div class="border-t pt-4">
          <div class="flex justify-between items-center mb-4">
            <h3 class="text-lg font-medium">Centros de Custo</h3>
            <button
              type="button"
              @click="addCostCenter"
              class="px-2 py-1 bg-primary-dark text-white rounded-md hover:bg-primary-dark/90 text-sm"
            >
              Adicionar Centro de Custo
            </button>
          </div>
          <div v-for="(center, index) in supplier.costCenters" :key="index" class="mb-4 p-4 bg-gray-50 rounded-lg relative">
            <button
              type="button"
              @click="removeCostCenter(index)"
              class="absolute top-2 right-2 text-red-600 hover:text-red-800"
            >
              <i class="pi pi-times"></i>
            </button>
            <div class="grid grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700">Centro de Custo</label>
                <select
                  v-model="center.costCenterId"
                  @change="handleCostCenterChange(center)"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                >
                  <option value="">Selecione um centro de custo</option>
                  <option v-for="cc in costCenters" :key="cc.id" :value="cc.id">
                    {{ cc.name }}
                  </option>
                </select>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700">Tipo</label>
                <select
                  v-model="center.costTypeId"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-dark focus:ring-primary-dark"
                  required
                >
                  <option value="">Selecione um tipo</option>
                  <option v-for="type in getTypesForCenter(center.costCenterId)" :key="type.id" :value="type.id">
                    {{ type.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>
        </div>

        <!-- Botões -->
        <div class="flex justify-end space-x-3">
          <button
            type="button"
            @click="hideDialog"
            class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            type="submit"
            class="px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-dark/90"
            :disabled="isLoading"
          >
            <i v-if="isLoading" class="pi pi-spin pi-spinner mr-2"></i>
            Salvar
          </button>
        </div>
      </form>
    </Dialog>

    <!-- Modal de Edição -->
    <Dialog
      v-model:visible="showEditDialog"
      modal
      header="Editar Fornecedor"
      :style="{ width: '80vw' }"
      :closable="true"
      @hide="hideDialog"
    >
      <form @submit.prevent="saveSupplier" class="space-y-6" v-if="supplier">
        <!-- Same content as the new supplier modal -->
      </form>
    </Dialog>

    <!-- Confirm Delete Dialog -->
    <Dialog
      v-model:visible="showConfirmDelete"
      modal
      header="Confirmar Exclusão"
      :style="{ width: '30vw' }"
      :closable="true"
    >
      <p class="mb-4">Tem certeza que deseja excluir este fornecedor?</p>
      <div class="flex justify-end space-x-3">
        <button
          @click="showConfirmDelete = false"
          class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          :disabled="loading"
        >
          Cancelar
        </button>
        <button
          @click="deleteSupplier(supplierToDelete)"
          class="px-4 py-2 bg-primary-dark text-white rounded-md hover:bg-primary-dark/90 flex items-center"
          :disabled="loading"
        >
          <i v-if="loading" class="pi pi-spin pi-spinner mr-2"></i>
          {{ loading ? 'Excluindo...' : 'Excluir' }}
        </button>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useSuppliersStore } from '../../stores/suppliers'
import { useCostCenterStore } from '../../stores/costCenter'
import { useCurrentOrganizationStore } from '../../stores/currentOrganization'
import { validateCNPJ } from '../../utils/validators'
import axios from 'axios'
import Dialog from 'primevue/dialog'
import ConfirmDialog from '../../components/common/ConfirmDialog.vue'
import { getBankList, type Bank } from '../../services/bank.service'
import type { Supplier, BankAccount, CostCenter } from '@/firebase/services/suppliers.service'

// Estado local
const loading = ref(false)
const isLoading = ref(false)
const supplierDialog = ref(false)
const showEditDialog = ref(false)
const showConfirmDelete = ref(false)
const supplierToDelete = ref<string | null>(null)
const loadError = ref<string | null>(null)
const banks = ref<Bank[]>([])
const editingSupplier = ref(null)

// Stores
const supplierStore = useSuppliersStore()
const costCenterStore = useCostCenterStore()
const currentOrganizationStore = useCurrentOrganizationStore()

// Computed
const suppliers = computed(() => supplierStore.suppliers)
const costCenters = computed(() => costCenterStore.costCenters)
const currentOrganizationId = computed(() => currentOrganizationStore.currentOrganization?.id)

// Função para obter tipos de um centro específico
const getTypesForCenter = (centerId: string) => {
  if (!centerId) return []
  const center = costCenters.value.find(cc => cc.id === centerId)
  return center?.types || []
}

// Função para limpar tipo quando centro é alterado
const handleCostCenterChange = (center: any) => {
  center.costTypeId = ''
}

// Watch para carregar tipos quando mudar o centro de custo
watch(costCenters, (newCenters) => {
  if (supplier.value.costCenters) {
    supplier.value.costCenters.forEach(center => {
      if (center.costCenterId) {
        const centerObj = newCenters.find(cc => cc.id === center.costCenterId)
        if (!centerObj?.types?.find(t => t.id === center.costTypeId)) {
          center.costTypeId = ''
        }
      }
    })
  }
}, { deep: true })

// Watch para carregar centros de custo quando a organização mudar
watch(currentOrganizationId, async (newOrgId) => {
  if (newOrgId) {
    await costCenterStore.fetchCostCenters(newOrgId)
  }
})

// Estado local
const defaultSupplier = {
  name: '',
  email: '',
  cnpj: '',
  phone: '',
  address: {
    cep: '',
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: ''
  },
  costCenters: [{
    costCenterId: '',
    costTypeId: ''
  }],
  bankAccounts: [{
    bank: '',
    agency: '',
    account: '',
    type: 'checking',
    pixKey: ''
  }]
}

// Estado do novo fornecedor
const supplier = ref(JSON.parse(JSON.stringify(defaultSupplier)))

// Funções de manipulação de contas bancárias
const addBankAccount = () => {
  supplier.value.bankAccounts.push({
    bank: '',
    agency: '',
    account: '',
    type: 'checking',
    pixKey: ''
  })
}

const removeBankAccount = (index: number) => {
  supplier.value.bankAccounts.splice(index, 1)
}

// Funções de manipulação de centros de custo
const addCostCenter = () => {
  supplier.value.costCenters.push({
    costCenterId: '',
    costTypeId: ''
  })
}

const removeCostCenter = (index: number) => {
  supplier.value.costCenters.splice(index, 1)
}

// Funções de manipulação de fornecedores
const openNewSupplierDialog = async () => {
  if (currentOrganizationId.value) {
    await costCenterStore.fetchCostCenters(currentOrganizationId.value)
  }
  supplier.value = JSON.parse(JSON.stringify(defaultSupplier))
  supplierDialog.value = true
}

const hideDialog = () => {
  supplierDialog.value = false
  showEditDialog.value = false
}

const editSupplier = async (data: Supplier) => {
  if (currentOrganizationId.value) {
    await costCenterStore.fetchCostCenters(currentOrganizationId.value)
    
    // Convert the data to match our form structure
    const formattedData = {
      ...data,
      costCenters: data.costCenters?.map(cc => ({
        costCenterId: cc.costCenterId,
        costTypeId: cc.costTypeId
      })) || [{
        costCenterId: '',
        costTypeId: ''
      }]
    }
    
    supplier.value = formattedData
  }
  supplierDialog.value = true
}

const saveSupplier = async () => {
  isLoading.value = true

  try {
    const supplierData = {
      ...supplier.value,
      costCenters: supplier.value.costCenters.map(cc => ({
        costCenterId: cc.costCenterId,
        costTypeId: cc.costTypeId
      }))
    }

    if (supplier.value.id) {
      await supplierStore.updateSupplier(supplier.value.id, supplierData)
    } else {
      await supplierStore.createSupplier(supplierData)
    }

    await loadSuppliers()
    hideDialog()
  } catch (error) {
    console.error('Error saving supplier:', error)
  } finally {
    isLoading.value = false
  }
}

const confirmDeleteSupplier = (data: Supplier) => {
  supplierToDelete.value = data.id
  showConfirmDelete.value = true
}

const deleteSupplier = async () => {
  try {
    await supplierStore.deleteSupplier(supplierToDelete.value!)
    await loadSuppliers()
  } catch (error) {
    console.error('Error deleting supplier:', error)
  } finally {
    showConfirmDelete.value = false
  }
}

const loadSuppliers = async () => {
  try {
    await supplierStore.getSuppliers()
  } catch (error) {
    console.error('Error loading suppliers:', error)
  }
}

const formatCNPJ = (cnpj: string) => {
  if (!cnpj) return ''
  const clean = cnpj.replace(/[^\d]/g, '')
  return clean.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
}

const formatPhone = (phone: string) => {
  if (!phone) return ''
  const clean = phone.replace(/[^\d]/g, '')
  if (clean.length === 11) {
    return clean.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
  }
  if (clean.length === 10) {
    return clean.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3')
  }
  return phone
}

const fetchCNPJData = async () => {
  try {
    const cnpj = supplier.value.cnpj.replace(/[^\d]/g, '')
    const response = await axios.get(`https://publica.cnpj.ws/cnpj/${cnpj}`)

    if (response.status !== 200) {
      throw new Error('Erro ao buscar dados do CNPJ')
    }

    const data = response.data

    supplier.value = {
      ...supplier.value,
      name: data.razao_social,
      email: data.email || supplier.value.email,
      phone: data.telefone1 || supplier.value.phone,
      address: {
        cep: data.estabelecimento.cep,
        street: data.estabelecimento.logradouro,
        number: data.estabelecimento.numero,
        complement: data.estabelecimento.complemento,
        neighborhood: data.estabelecimento.bairro,
        city: data.estabelecimento.cidade.nome,
        state: data.estabelecimento.estado.sigla
      }
    }
  } catch (error) {
    console.error('Error fetching CNPJ data:', error)
  }
}

// Carrega os dados iniciais
onMounted(async () => {
  if (currentOrganizationId.value) {
    await loadSuppliers()
    await costCenterStore.fetchCostCenters(currentOrganizationId.value)
    const bankList = await getBankList()
    banks.value = bankList
  }
})

// Watch para buscar CNPJ quando documento é alterado
watch(() => supplier.value.cnpj, async (newCnpj) => {
  const cleanCnpj = newCnpj.replace(/[^\d]/g, '')
  if (cleanCnpj.length === 14) {
    await fetchCNPJData()
  }
})
</script>

<style scoped>
.p-dialog .p-dialog-content {
  padding: 2rem;
}

input:disabled {
  background-color: #f3f4f6;
  cursor: not-allowed;
}
</style>